import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAgentsList } from "@/hooks/agent/useAgents";
import { useBrain } from "@/contexts/ui/brain";
import { DEFAULT_CONVERSATION_TIME_RANGE } from "@/constants/conversationFilters";
import { buildInboxDigestPrompt, DIGEST_CONVERSATION_LIMIT } from "@/lib/inboxDigest";
import { ConversationService } from "@/services/conversation/ConversationService";
import type { Conversation, InboxTabContentProps } from "@/types/conversation/conversation";
import ConversationsPage from "@/pages/UserHub/Conversations";
import ViewsPage from "@/pages/UserHub/Inbox/Views";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

type Tab = {
  id: string;
  label: string;
  /** Shown instead of the label on phones, where the long one wraps. */
  shortLabel?: string;
  genieIcon: GenieIconName;
};

const TABS: Tab[] = [
  { id: "conversations", label: "Conversations",                genieIcon: "chat" },
  { id: "views",         label: "Views — Filter conversations by topic or need", shortLabel: "Views", genieIcon: "eye" },
];

const DEFAULT_TAB = "conversations";

// Tab ids that used to exist and got folded into Views. Any URL hitting one
// of these redirects to ?tab=views so inbound links don't 404.
const REMOVED_TAB_IDS = new Set([
  "leads", "support",
  "bookings", "orders", "quotes", "surveys",
  "forms", "interviews", "reviews", "training",
  "hide",
]);

const isValidTab = (value: string | null, tabs: Tab[]): value is string => {
  if (!value) return false;
  return tabs.some((t) => t.id === value);
};

const InboxPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: agents = [] } = useAgentsList();
  const { openBrain } = useBrain();
  const [askPending, setAskPending] = useState(false);

  // The Conversations tab reports what it shows, so the digest matches the
  // filters on screen; from Views, the most recent conversations are fetched.
  const visibleConversationsRef = useRef<Conversation[]>([]);
  const handleConversationsChange = useCallback((conversations: Conversation[]) => {
    visibleConversationsRef.current = conversations;
  }, []);
  const rawTab = searchParams.get("tab");
  const visibleTabs = TABS;

  // Redirect old persona-tab URLs into Views.
  useEffect(() => {
    if (rawTab && REMOVED_TAB_IDS.has(rawTab)) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", "views");
      setSearchParams(next, { replace: true });
    }
  }, [rawTab, searchParams, setSearchParams]);

  const activeTabId = isValidTab(rawTab, visibleTabs) ? rawTab! : DEFAULT_TAB;
  const activeTab = TABS.find((t) => t.id === activeTabId) ?? TABS[0];

  const handleAskInbox = async () => {
    if (activeTabId === "conversations" && visibleConversationsRef.current.length > 0) {
      openBrain(buildInboxDigestPrompt(visibleConversationsRef.current));
      return;
    }
    setAskPending(true);
    try {
      const { data } = await ConversationService.getAll({
        agentIds: agents.map((a) => a.id),
        timeRange: DEFAULT_CONVERSATION_TIME_RANGE,
        limit: DIGEST_CONVERSATION_LIMIT,
        excludeMailbox: true,
      });
      if (data.length === 0) {
        toast.info("No conversations yet to ask about.");
        return;
      }
      openBrain(buildInboxDigestPrompt(data));
    } catch {
      toast.error("Couldn't load your conversations. Try again.");
    } finally {
      setAskPending(false);
    }
  };

  const handleSelectTab = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === DEFAULT_TAB) {
      next.delete("tab");
    } else {
      next.set("tab", id);
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto max-w-[1920px] w-full px-4 py-5 md:py-6">
        {/*
          WorkspaceSwitcher is fixed at `right-6 top-5` (md and up) and owns the
          top-right corner, so the header actions have to stop short of it.
        */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between md:pr-[140px]">
          <InboxTabBar tabs={visibleTabs} activeTabId={activeTabId} onSelect={handleSelectTab} />
          <div className="flex shrink-0 items-center gap-2">
            {agents.length > 0 && (
              <button
                type="button"
                onClick={handleAskInbox}
                disabled={askPending}
                className="h-10 p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm hover:shadow-md hover:shadow-genie-primary/25 transition-all disabled:opacity-70"
              >
                <span className="flex h-full items-center gap-2 rounded-[10px] bg-white px-3.5 text-sm font-semibold">
                  {askPending ? (
                    <Loader2 className="h-4 w-4 animate-spin text-genie-primary" />
                  ) : (
                    <GenieIcon name="genie-lamp" size={16} className="h-4 w-4" />
                  )}
                  <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
                    Ask about your inbox
                  </span>
                </span>
              </button>
            )}
          </div>
        </div>

        <LiveTabContent
          tabId={activeTab.id}
          onConversationsChange={handleConversationsChange}
        />
      </div>
    </div>
  );
};

const InboxTabBar = ({ tabs, activeTabId, onSelect }: { tabs: Tab[]; activeTabId: string; onSelect: (id: string) => void }) => {
  // The Views tab is the product's standout capability, so it renders as a
  // gradient "feature pill" instead of a plain tab — bold even when inactive so
  // it reads as a real feature to discover, with a selected-ring when active.
  const renderViewsTab = (tab: Tab, isActive: boolean) => (
    <button
      key={tab.id}
      onClick={() => onSelect(tab.id)}
      className={`group flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all duration-200 hover:scale-[1.03] hover:shadow-xl hover:shadow-genie-primary/40 ${
        isActive
          ? "shadow-lg shadow-genie-primary/40 ring-2 ring-[#4E9CFF]/50 ring-offset-2 ring-offset-[#F9FAFC]"
          : "shadow-md shadow-genie-primary/30"
      }`}
    >
      <GenieIcon
        name={tab.genieIcon}
        size={16}
        color="#ffffff"
        label=""
        className="shrink-0 transition-transform duration-200 group-hover:scale-110"
      />
      {tab.shortLabel ? (
        <>
          <span className="sm:hidden">{tab.shortLabel}</span>
          <span className="hidden sm:inline">{tab.label}</span>
        </>
      ) : (
        <span>{tab.label}</span>
      )}
    </button>
  );

  const renderTab = (tab: Tab) => {
    const isActive = tab.id === activeTabId;

    if (tab.id === "views") {
      return renderViewsTab(tab, isActive);
    }

    if (isActive) {
      return (
        <button
          key={tab.id}
          onClick={() => onSelect(tab.id)}
          className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-md shadow-genie-primary/20"
        >
          <span className="flex items-center gap-2 px-3.5 py-2 rounded-[10px] bg-white text-sm font-semibold text-[#222B45]">
            <GenieIcon name={tab.genieIcon} size={16} className="h-4 w-4" />
            {tab.label}
          </span>
        </button>
      );
    }

    return (
      <button
        key={tab.id}
        onClick={() => onSelect(tab.id)}
        className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium border bg-white border-[#E5EAF0] text-gray-700 hover:border-genie-primary/40 hover:text-genie-primary transition-all"
      >
        <GenieIcon name={tab.genieIcon} size={16} className="h-4 w-4" />
        {tab.label}
      </button>
    );
  };

  return <div className="flex flex-wrap items-center gap-2">{tabs.map(renderTab)}</div>;
};

const LiveTabContent = ({ tabId, onConversationsChange }: InboxTabContentProps) => {
  if (tabId === "conversations") {
    return <ConversationsPage embedded onConversationsChange={onConversationsChange} />;
  }
  if (tabId === "views") return <ViewsPage embedded />;
  return null;
};

export default InboxPage;
